import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { CommonRouteQueryComposable } from '@/composables/routeQueries/useCommonRouteQuery';
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type { ARServiceItem } from '@/models/airRoad';

import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { isCancel } from 'axios';
import { format, toZonedTime } from 'date-fns-tz';
import _cloneDeep from 'lodash/cloneDeep';

import { useLoaderStore } from '@/stores/useLoaderStore';
import * as airRoadHelpers from '@/helpers/airRoad';
import {
  fetchGetAddressBookAddresses,
  fetchGetAirroadJobDetailed,
  fetchPostAirroadBookingSend,
} from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';

export interface NewAirRoadViewDataComposable {
  isFetchingAirRoadBookingJob: Readonly<Ref<boolean>>;
  isFetchingAddresses: Readonly<Ref<boolean>>;
  isFetchingAirRoadJobCreateData: Readonly<Ref<boolean>>;

  airRoadBookingJob: Readonly<
    Ref<ReadonlyDeep<openapi.AirroadJobDetailedContentOpenapi> | undefined>
  >;
  addresses: Readonly<
    Ref<
      | ReadonlyDeep<openapi.AddressBookAddressesListContentOpenapi['results']>
      | undefined
    >
  >;
  airRoadJobCreateData: Readonly<
    Ref<ReadonlyDeep<openapi.AirroadSingleCreateContentOpenapi> | undefined>
  >;

  airRoadBookingJobError: Readonly<Ref<unknown | undefined>>;
  addressesError: Readonly<Ref<unknown | undefined>>;
  airRoadJobCreateDataError: Readonly<Ref<unknown | undefined>>;

  refreshAirRoadBookingJob: () => Promise<void>;
  refreshAddresses: () => Promise<void>;
  refreshAirRoadJobCreateData: (
    bookingState:
      | ReadonlyDeep<{
          params: openapi.AirroadSingleCreateQueryOpenapi;
          data: openapi.AirroadSingleCreateBodyOpenapi;
          _deliverySpecialOptions: Record<string, ARServiceItem>;
          _pickupSpecialOptions: Record<string, ARServiceItem>;
        }>
      | undefined,
  ) => Promise<void>;

  reload: () => NewAirRoadViewDataComposable;
}

export default function useNewAirRoadViewData(
  routeQuery: CommonRouteQueryComposable,
): NewAirRoadViewDataComposable {
  const route = useRoute();
  const { queryJobId, queryJobDate } = routeQuery;

  const loaderStore = useLoaderStore();

  let airRoadBookingJobController: AbortController | undefined;
  let addressesController: AbortController | undefined;
  let createAirRoadJobController: AbortController | undefined;

  const airRoadBookingJobFetchCounter = ref(0);
  const addressesFetchCounter = ref(0);
  const airRoadJobCreateDataFetchCounter = ref(0);

  const isFetchingAirRoadBookingJob = computed(
    () => !!airRoadBookingJobFetchCounter.value,
  );
  const isFetchingAddresses = computed(() => !!addressesFetchCounter.value);
  const isFetchingAirRoadJobCreateData = computed(
    () => !!airRoadJobCreateDataFetchCounter.value,
  );

  const airRoadBookingJob =
    ref<ReadonlyDeep<openapi.AirroadJobDetailedContentOpenapi>>();
  const addresses =
    ref<
      ReadonlyDeep<openapi.AddressBookAddressesListContentOpenapi['results']>
    >();
  const airRoadJobCreateData =
    ref<ReadonlyDeep<openapi.AirroadSingleCreateContentOpenapi>>();

  const airRoadBookingJobError = ref();
  const addressesError = ref();
  const airRoadJobCreateDataError = ref();

  function clear(): void {
    if (airRoadBookingJobController) airRoadBookingJobController.abort();
    if (addressesController) addressesController.abort();
    if (createAirRoadJobController) createAirRoadJobController.abort();
  }

  onUnmounted(() => {
    clear();
  });

  async function _fetchAirRoadBookingJob(): Promise<
    ReadonlyDeep<openapi.AirroadJobDetailedContentOpenapi> | undefined
  > {
    if (airRoadBookingJobController) airRoadBookingJobController.abort();
    const jobId = queryJobId.value;
    const jobDate = queryJobDate.value;
    if (typeof jobId !== 'number') return;
    if (!jobDate) return;
    airRoadBookingJobController = new AbortController();
    return await fetchGetAirroadJobDetailed(
      {
        job_id: jobId,
        job_date: jobDate,
      },
      {
        signal: airRoadBookingJobController.signal,
      },
    )
      .then(function (data) {
        airRoadBookingJobError.value = undefined;
        return data;
      })
      .catch(function (err: unknown) {
        if (isCancel(err)) return undefined;
        airRoadBookingJobError.value = err;
        throw err;
      });
  }

  async function _fetchAddresses(): Promise<
    | ReadonlyDeep<openapi.AddressBookAddressesListContentOpenapi['results']>
    | undefined
  > {
    if (addressesController) addressesController.abort();
    addressesController = new AbortController();
    return await fetchGetAddressBookAddresses(
      {
        warehouse: true,
        page: 1,
      },
      {
        signal: addressesController.signal,
      },
    )
      .then(function (data) {
        addressesError.value = undefined;
        return data.results;
      })
      .catch(function (err: unknown) {
        if (isCancel(err)) return undefined;
        addressesError.value = err;
        throw err;
      });
  }

  function _getSendSpecialOption(
    arServiceItem: ARServiceItem,
    type: 'Delivery' | 'Pickup',
    zoneString: string,
  ): ReadonlyDeep<
    openapi.AirroadSingleCreateBodyOpenapi['special_options'][0]
  > {
    const specialOption: openapi.AirroadSingleCreateBodyOpenapi['special_options'][0] =
      {
        is_pickup: false,
        special_option_id: arServiceItem.specialOptionId,
        code: arServiceItem.code,
      };
    if (type === 'Delivery') {
      specialOption.is_pickup = false;
      // TODO
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    } else if (type === 'Pickup') {
      specialOption.is_pickup = true;
    }
    if (arServiceItem.contact_name != null) {
      specialOption.name = arServiceItem.contact_name;
    }
    if (arServiceItem.contact_phone != null) {
      specialOption.phone = arServiceItem.contact_phone;
    }
    if (arServiceItem.description != null) {
      specialOption.description = arServiceItem.description;
    }
    if (arServiceItem._date != null && arServiceItem._time != null) {
      specialOption.time = airRoadHelpers.getARServiceItemDatetime(
        arServiceItem._date,
        arServiceItem._time,
        zoneString,
      );
    }
    if (arServiceItem._dateFrom != null) {
      specialOption.first_date = arServiceItem._dateFrom;
    }
    if (arServiceItem._dateTo != null) {
      specialOption.second_date = arServiceItem._dateTo;
    }
    return specialOption;
  }

  function _getSendParams(
    bookingState: ReadonlyDeep<{
      params: openapi.AirroadSingleCreateQueryOpenapi;
    }>,
  ): ReadonlyDeep<openapi.AirroadSingleCreateQueryOpenapi> {
    return bookingState.params;
  }

  function _getSendData(
    bookingState: ReadonlyDeep<{
      data: openapi.AirroadSingleCreateBodyOpenapi;
      _deliverySpecialOptions: Record<string, ARServiceItem>;
      _pickupSpecialOptions: Record<string, ARServiceItem>;
    }>,
  ): ReadonlyDeep<openapi.AirroadSingleCreateBodyOpenapi> {
    const arStops = [];
    const specialOptions: openapi.AirroadSingleCreateBodyOpenapi['special_options'] =
      [];
    if (bookingState.data.ar_booked_leg === 1) {
      arStops.push(bookingState.data.ar_stops[0]);
      arStops.push(bookingState.data.ar_stops[1]);
    } else if (bookingState.data.ar_booked_leg === 2) {
      arStops.push(bookingState.data.ar_stops[1]);
      arStops.push(bookingState.data.ar_stops[2]);
    }
    const stopTimeZone = airRoadHelpers.getStopTimeZone(
      airRoadBookingJob.value?.stops[bookingState.data.ar_booked_leg - 1],
    );
    const zoneString = format(toZonedTime(new Date(), stopTimeZone), 'xxx', {
      timeZone: stopTimeZone,
    });
    Object.entries(bookingState._deliverySpecialOptions).forEach(
      ([_, specialOption]) => {
        if (!specialOption.chosen) return;
        specialOptions.push(
          _getSendSpecialOption(specialOption, 'Delivery', zoneString),
        );
      },
    );
    Object.entries(bookingState._pickupSpecialOptions).forEach(
      ([_, specialOption]) => {
        if (!specialOption.chosen) return;
        specialOptions.push(
          _getSendSpecialOption(specialOption, 'Pickup', zoneString),
        );
      },
    );
    return {
      job_id: bookingState.data.job_id,
      job_date: bookingState.data.job_date,
      ar_booked_leg: bookingState.data.ar_booked_leg,
      items: bookingState.data.items.map((_item) => {
        const item = _cloneDeep(
          _item,
        ) as openapi.AirroadSingleCreateBodyOpenapi['items'][0];
        if (!item.products) return item;
        item.products = item.products.map((product) => {
          if (product.extra) {
            return {
              extra: true,
            };
          } else {
            return {
              product_id: product.product_id,
              quantity: product.quantity,
            };
          }
        });
        return item;
      }),
      ar_stops: arStops,
      special_options: specialOptions,
    };
  }

  async function _fetchAirRoadJobCreateData(
    bookingState:
      | ReadonlyDeep<{
          params: openapi.AirroadSingleCreateQueryOpenapi;
          data: openapi.AirroadSingleCreateBodyOpenapi;
          _deliverySpecialOptions: Record<string, ARServiceItem>;
          _pickupSpecialOptions: Record<string, ARServiceItem>;
        }>
      | undefined,
  ): Promise<
    ReadonlyDeep<openapi.AirroadJobDetailedContentOpenapi> | undefined
  > {
    if (createAirRoadJobController) createAirRoadJobController.abort();
    if (!bookingState) return;
    const sendParams = _getSendParams(bookingState);
    const sendData = _getSendData(bookingState);
    createAirRoadJobController = new AbortController();
    return await fetchPostAirroadBookingSend(sendParams, sendData, {
      signal: createAirRoadJobController.signal,
    })
      .then(function (data) {
        airRoadJobCreateDataError.value = undefined;
        return data;
      })
      .catch(function (err: unknown) {
        if (isCancel(err)) return undefined;
        airRoadJobCreateDataError.value = err;
        throw err;
      });
  }

  async function refreshAirRoadBookingJob(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    airRoadBookingJobFetchCounter.value++;
    try {
      airRoadBookingJob.value = await _fetchAirRoadBookingJob();
    } finally {
      airRoadBookingJobFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  async function refreshAddresses(): Promise<void> {
    loaderStore.increaseGlobalLoader();
    addressesFetchCounter.value++;
    try {
      addresses.value = await _fetchAddresses();
    } finally {
      addressesFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  async function refreshAirRoadJobCreateData(
    bookingState:
      | ReadonlyDeep<{
          params: openapi.AirroadSingleCreateQueryOpenapi;
          data: openapi.AirroadSingleCreateBodyOpenapi;
          _deliverySpecialOptions: Record<string, ARServiceItem>;
          _pickupSpecialOptions: Record<string, ARServiceItem>;
        }>
      | undefined,
  ): Promise<void> {
    loaderStore.increaseGlobalLoader();
    airRoadJobCreateDataFetchCounter.value++;
    try {
      airRoadJobCreateData.value =
        await _fetchAirRoadJobCreateData(bookingState);
    } finally {
      airRoadJobCreateDataFetchCounter.value--;
      loaderStore.decreaseGlobalLoader();
    }
  }

  function reload(): NewAirRoadViewDataComposable {
    void Promise.all([refreshAirRoadBookingJob(), refreshAddresses()]);
    return {
      isFetchingAirRoadBookingJob,
      isFetchingAddresses,
      isFetchingAirRoadJobCreateData,
      airRoadBookingJob,
      addresses,
      airRoadJobCreateData,
      airRoadBookingJobError,
      addressesError,
      airRoadJobCreateDataError,
      refreshAirRoadBookingJob,
      refreshAddresses,
      refreshAirRoadJobCreateData,
      reload,
    };
  }

  watch(
    [queryJobId, queryJobDate],
    async function (newValues, oldValues) {
      if (route.name !== routeNames.AIR_ROAD_JOBS_NEW) return;
      reload();
    },
    { immediate: true },
  );

  return {
    isFetchingAirRoadBookingJob,
    isFetchingAddresses,
    isFetchingAirRoadJobCreateData,
    airRoadBookingJob,
    addresses,
    airRoadJobCreateData,
    airRoadBookingJobError,
    addressesError,
    airRoadJobCreateDataError,
    refreshAirRoadBookingJob,
    refreshAddresses,
    refreshAirRoadJobCreateData,
    reload,
  };
}
